import { useState } from 'react';
import { Box, Typography, Button, Grid, Alert, Chip, Paper, IconButton, Tooltip } from '@mui/material';
import { Add, Refresh, School, TrendingUp, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDataProvider } from 'react-admin';
import { useWorkflowList, WorkflowCard, WorkflowFilters } from '@/components/workflow';
import type { WorkflowInstance } from '@/components/workflow';

export const SkillsDevelopmentWorkflowManager = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [filters, setFilters] = useState<{ status?: string[]; workflowType?: string[]; search?: string }>({
    workflowType: ['training'],
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { workflows, total, isLoading, error, refetch } = useWorkflowList({
    dataProvider,
    resource: 'workflow-instances',
    filters,
    perPage: 50,
  });

  const activeCount = workflows.filter(w => w.status === 'active').length;
  const pausedCount = workflows.filter(w => w.status === 'paused').length;
  const completedCount = workflows.filter(w => w.status === 'completed' || w.status === 'cancelled').length;

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    refetch();
  };

  const handleWorkflowOpen = (workflow: WorkflowInstance) => {
    navigate('/wizard/training');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Skills Development Workflows
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage training and skills development processes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #1976d2' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Active
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {activeCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #ed6c02' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Paused
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
              {pausedCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #2e7d32' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Completed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {completedCount}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/wizard/training')}
        >
          Log Training
        </Button>
        <Button
          variant="outlined"
          startIcon={<TrendingUp />}
          onClick={() => navigate('/wizard/wsp-atr')}
        >
          Generate WSP/ATR
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <WorkflowFilters />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Typography color="text.secondary">Loading workflows...</Typography>
      ) : workflows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <School sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Training Workflows Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No training workflows match your current filters or no training workflows have been initiated yet.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/wizard/training')}
            >
              Log Training
            </Button>
            <Button
              variant="outlined"
              startIcon={<TrendingUp />}
              onClick={() => navigate('/wizard/wsp-atr')}
            >
              Generate WSP/ATR
            </Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {workflows.map(workflow => (
            <Grid key={workflow.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <WorkflowCard
                workflow={workflow}
                onOpen={handleWorkflowOpen}
                showActions={false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Total: {total} workflow(s)
        </Typography>
        <Chip
          icon={<School />}
          label="Skills Development"
          size="small"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};